import {
  ClickIcon,
  EyeIcon,
  MailClickIcon,
  MailIcon,
  MailOpenIcon,
  MailSendIcon,
  MobilePhoneIcon,
  PushClickedIcon,
  PushSentIcon,
  PushTappedIcon,
  SendMessageIcon,
  SmsIcon,
  SmsOpenedIcon,
  SmsSentIcon,
  SmsTappedIcon,
} from "@hightouch/htui";
import { AB_TEST_MEASUREMENT_WINDOW_DAYS } from "@hightouch/lib/cep/ab-test/types";

import { ChartGranularity } from "src/components/charts/chart-utils";
import {
  formatDateLong,
  formatMonthDay,
  formatRelativeShort,
} from "src/utils/time";

import {
  ChannelType,
  VolumeCapReset,
  SendRatePeriod,
  DurationUnit,
} from "src/types/cep";
import { DerivedCampaignStatus } from "src/graphql";
import type { CampaignType } from "src/cep/campaigns/campaign-detail/form-types";
import type { JourneyState } from "src/pages/journeys/types";
import { hasTestEnded } from "src/cep/messages/components/message-builder/ab-tests/ab-test-status";
import { EngagementMetric, getSoleChannel } from "./types";

export type StatsTimeRange = "7d" | "30d" | "all";

export const STATS_TIME_RANGE_CONFIG: Record<
  StatsTimeRange,
  { days: number; subtitle: string; granularity: ChartGranularity }
> = {
  "7d": {
    days: 7,
    subtitle: "All messages, Last 7 days",
    granularity: ChartGranularity.SevenDays,
  },
  "30d": {
    days: 30,
    subtitle: "All messages, Last 30 days",
    granularity: ChartGranularity.ThirtyDays,
  },
  all: {
    days: 365 * 10,
    subtitle: "All messages, All time",
    granularity: ChartGranularity.NinetyDays,
  },
};

export const MAX_TEXT_AREA_LENGTH = 140;

export const CHANNELS: Record<
  ChannelType,
  { label: string; icon: React.ComponentType }
> = {
  [ChannelType.Email]: {
    label: "Email",
    icon: MailIcon,
  },
  [ChannelType.Sms]: {
    label: "SMS",
    icon: SmsIcon,
  },
  [ChannelType.Push]: {
    label: "Push",
    icon: MobilePhoneIcon,
  },
};

export const CHANNEL_OPTIONS: {
  value: ChannelType;
  label: string;
  icon: React.ComponentType;
  isDisabled?: boolean;
}[] = Object.entries(CHANNELS).map(([value, { label, icon }]) => ({
  value: value as ChannelType,
  label,
  icon,
}));

export const ChannelIconMapping: Record<ChannelType, React.ComponentType> = {
  [ChannelType.Email]: MailIcon,
  [ChannelType.Sms]: SmsIcon,
  [ChannelType.Push]: MobilePhoneIcon,
};

export const METRIC_ICONS: Record<
  ChannelType,
  Record<EngagementMetric, React.ComponentType>
> = {
  [ChannelType.Email]: {
    [EngagementMetric.Sent]: MailSendIcon,
    [EngagementMetric.Delivered]: MailSendIcon,
    [EngagementMetric.Opened]: MailOpenIcon,
    [EngagementMetric.Clicked]: MailClickIcon,
  },
  [ChannelType.Sms]: {
    [EngagementMetric.Sent]: SmsSentIcon,
    [EngagementMetric.Delivered]: SmsSentIcon,
    [EngagementMetric.Opened]: SmsOpenedIcon,
    [EngagementMetric.Clicked]: SmsTappedIcon,
  },
  [ChannelType.Push]: {
    [EngagementMetric.Sent]: PushSentIcon,
    [EngagementMetric.Delivered]: PushSentIcon,
    [EngagementMetric.Opened]: PushTappedIcon,
    [EngagementMetric.Clicked]: PushClickedIcon,
  },
};

// A mixed or unknown channel set (a journey's "All messages" scope spanning
// email + SMS) has no honest channel icon.
const NEUTRAL_METRIC_ICONS: Record<EngagementMetric, React.ComponentType> = {
  [EngagementMetric.Sent]: SendMessageIcon,
  [EngagementMetric.Delivered]: SendMessageIcon,
  [EngagementMetric.Opened]: EyeIcon,
  [EngagementMetric.Clicked]: ClickIcon,
};

export function getMetricIcon(
  channels: ChannelType | ChannelType[] | string | null | undefined,
  metric: EngagementMetric,
): React.ComponentType {
  const channel = getSoleChannel(channels);
  return channel ? METRIC_ICONS[channel][metric] : NEUTRAL_METRIC_ICONS[metric];
}

export const DEFAULT_COOLDOWN_HOURS = 168;

export const VOLUME_CAP_RESET_OPTIONS: {
  label: string;
  value: VolumeCapReset;
  description: string;
}[] = [
  {
    label: "for the campaign lifetime",
    value: "lifetime",
    description: "One limit for the entire campaign",
  },
  {
    label: "per scheduled send",
    value: "per_run",
    description: "Each send gets its own limit",
  },
];

export const SEND_RATE_PERIOD_OPTIONS: {
  label: string;
  value: SendRatePeriod;
}[] = [
  { label: "minute", value: "minute" },
  { label: "hour", value: "hour" },
  { label: "day", value: "day" },
];

// Why per_run is unavailable (shown as the disabled option message), or
// undefined when allowed — runless triggers have no run to reset on (CEP-1612).
export function getPerRunResetDisabledReason(
  campaignType: CampaignType | null | undefined,
): string | undefined {
  if (campaignType == null) return "Pick a schedule type first";
  switch (campaignType) {
    case "one_time":
    case "recurring":
      return undefined;
    case "api_triggered":
      return "API-triggered campaigns don't have scheduled sends";
    default: {
      // Exhaustiveness: adding a CampaignType breaks the build here until this
      // function decides whether the new type resets per scheduled send.
      const _exhaustive: never = campaignType;
      return _exhaustive;
    }
  }
}

export const DURATION_UNIT_OPTIONS: {
  label: string;
  value: DurationUnit;
}[] = [
  { label: "hours", value: "hours" },
  { label: "days", value: "days" },
];

export const EMAIL_PREVIEW_MAX_WIDTH = "2xl";
export const EMAIL_PREVIEW_MIN_WIDTH = "2xl";

export const PUSH_PREVIEW_MAX_WIDTH = "744px";

export const MINI_THUMBNAIL_SCALE = "6.6px";
export const MINI_THUMBNAIL_LINE_HEIGHT = "8.8px";

export const isDraftStatus = (status?: DerivedCampaignStatus) =>
  status != null && status === DerivedCampaignStatus.Draft;

/**
 * Campaign statuses where message content edits are allowed. Edits in
 * Scheduled/Running/Paused/Error **stage** as an unpublished draft (advance
 * `draft_version`); they go live only on an explicit Publish, when the
 * dispatcher's per-fragment claim picks up the new `published_version` for
 * not-yet-claimed fragments. Completed/Archived stay read-only (terminal).
 */
export const EDITABLE_CAMPAIGN_STATUSES = new Set<DerivedCampaignStatus>([
  DerivedCampaignStatus.Draft,
  DerivedCampaignStatus.Scheduled,
  DerivedCampaignStatus.Running,
  DerivedCampaignStatus.Paused,
  DerivedCampaignStatus.Error,
]);

export const isEditableCampaignStatus = (
  status: DerivedCampaignStatus | null | undefined,
): boolean => status != null && EDITABLE_CAMPAIGN_STATUSES.has(status);

/**
 * Campaign states in which a message running an A/B test may show results:
 * every state past activation, live and terminal alike (an errored campaign may
 * have partial sends worth reading). Scheduled precedes any send, but the card
 * still shows so the marketer can review the test setup — it captions itself
 * "Results will appear after this campaign's first send". Draft renders its own
 * overview layout without the Performance section, and Archived hides the
 * panel deliberately.
 */
const AB_TEST_RESULT_STATUSES = new Set<DerivedCampaignStatus>([
  DerivedCampaignStatus.Scheduled,
  DerivedCampaignStatus.Activated,
  DerivedCampaignStatus.Running,
  DerivedCampaignStatus.Paused,
  DerivedCampaignStatus.Completed,
  DerivedCampaignStatus.Error,
]);

/**
 * Whether the A/B results panel may render. Only a message that actually has an
 * A/B test earns the wider window above; without one the panel stays on the
 * Activated-only gate it has always used. Shared so the message overview and
 * the campaign detail Performance section can't drift apart.
 */
export const canShowCampaignAbTestResults = (
  status: DerivedCampaignStatus | null | undefined,
  hasAbTest: boolean,
): boolean =>
  hasAbTest
    ? status != null && AB_TEST_RESULT_STATUSES.has(status)
    : status === DerivedCampaignStatus.Activated;

/**
 * The journey mirror of {@link AB_TEST_RESULT_STATUSES}: every state past
 * activation. `draft`/`staged` precede a launch; `resetting` is on its way
 * back to draft.
 */
const AB_TEST_RESULT_JOURNEY_STATES = new Set<JourneyState>([
  "active",
  "draining",
  "paused",
  "off",
  // A journey mid-deletion still has its run data until cleanup finishes.
  "deleting",
]);

export const canShowJourneyAbTestResults = (
  journeyState: JourneyState | null | undefined,
  hasAbTest: boolean,
): boolean =>
  hasAbTest &&
  journeyState != null &&
  AB_TEST_RESULT_JOURNEY_STATES.has(journeyState);

/**
 * A/B results gate for the message overview layouts. Campaign takes precedence
 * over journey, mirroring `messageSurfaceInput`; a standalone message never
 * sends, so it has no results to show.
 */
export const canShowMessageAbTestResults = (args: {
  campaignInfo: { derivedStatus: DerivedCampaignStatus | undefined } | null;
  journeyState: JourneyState | null;
  hasAbTest: boolean;
}): boolean =>
  args.campaignInfo != null
    ? canShowCampaignAbTestResults(
        args.campaignInfo.derivedStatus,
        args.hasAbTest,
      )
    : canShowJourneyAbTestResults(args.journeyState, args.hasAbTest);

/**
 * Campaign states where promoting an A/B winner is offered — states that still
 * send or are about to. Narrower than {@link AB_TEST_RESULT_STATUSES}: terminal
 * Completed/Archived and Draft are excluded, since a winner only matters while
 * variants are still going out. Promoting never ends the test, so results keep
 * showing everywhere the wider gate allows.
 */
const PROMOTE_WINNER_CAMPAIGN_STATUSES = new Set<DerivedCampaignStatus>([
  DerivedCampaignStatus.Scheduled,
  DerivedCampaignStatus.Running,
  DerivedCampaignStatus.Paused,
]);

/**
 * The journey mirror: active journeys still send (`draining` flushes users
 * already held), so a winner promotion still takes effect on future sends.
 */
const PROMOTE_WINNER_JOURNEY_STATES = new Set<JourneyState>([
  "active",
  "draining",
]);

/**
 * Whether the surface is live enough to promote an A/B winner. Campaign takes
 * precedence over journey (mirroring `canShowMessageAbTestResults`); a
 * standalone message never sends, so it can never promote.
 */
export const canPromoteAbTestWinner = (args: {
  campaignStatus?: DerivedCampaignStatus | null;
  journeyState?: JourneyState | null;
}): boolean =>
  args.campaignStatus != null
    ? PROMOTE_WINNER_CAMPAIGN_STATUSES.has(args.campaignStatus)
    : args.journeyState != null &&
      PROMOTE_WINNER_JOURNEY_STATES.has(args.journeyState);

/**
 * How long a test keeps measuring after a winner is promoted. Shared with the
 * backend auto-end (`end-message-experiments.ts`) via `@hightouch/lib/cep` so
 * the promote window and the finish-sending window can't drift.
 */
export const PROMOTION_MEASUREMENT_WINDOW_DAYS =
  AB_TEST_MEASUREMENT_WINDOW_DAYS;

const AB_TEST_ELEMENT_LABELS: Record<string, string> = {
  subject_preheader: "Subject + Preheader",
  body: "Content",
};

/**
 * The tested element as the marketer named it. The element arrives from the
 * database as a free-form string, so an unrecognized one falls back to itself
 * rather than rendering blank. Shared so the results card and its details
 * drawer label the same experiment identically.
 */
export const abTestElementLabel = (element: string): string =>
  AB_TEST_ELEMENT_LABELS[element] ?? element;

/**
 * When a test ended, as the phrase that follows "ended": "today", "3h ago", or
 * "on Feb 2" past five weeks. Null when no end date was recorded.
 *
 * Never a clock time. `completed_at` is a scheduled end *date* — End test
 * stamps end-of-today — so rendering it as a timestamp would claim a precision
 * the column doesn't have (and read as 11:59 PM every time).
 */
export function abTestEndedWhen(completedAt: string | null): string | null {
  if (!completedAt) return null;
  const relative = formatRelativeShort(completedAt);
  if (!relative) return null;
  // Inside the first hour `formatRelativeShort` says "Just now", which reads as
  // a clock reading rather than a day.
  if (relative === "Just now") return "today";
  return relative.endsWith(" ago") ? relative : `on ${relative}`;
}

/**
 * How long ago a test ended, as a predicate reading off "Test …" or "Your
 * Subject A/B test …". Shared so the ended banner and the drawer's ended
 * marker can't drift onto different phrasings.
 */
export function abTestEndedPhrase(completedAt: string | null): string {
  const when = abTestEndedWhen(completedAt);
  return when ? `ended ${when}` : "has ended";
}

/**
 * The exact day a test ended, for the hover that every relative date owes. Date
 * only — `completed_at` is a scheduled end date, so a time would be invented.
 */
export function abTestEndedOn(completedAt: string | null): string | null {
  // `formatDateLong` ("August 15, 2026"), not the numeric default — a
  // month-name date can't be misread across regions.
  return completedAt ? formatDateLong(completedAt) : null;
}

/**
 * The same day without the year ("August 15"), for the list badge — it only
 * surfaces tests completed in the last two days, so the year is noise.
 */
export function abTestEndedOnShort(completedAt: string | null): string | null {
  return completedAt ? formatMonthDay(completedAt) : null;
}

/**
 * Whole days a test ran, from its start to its scheduled end. Rounded, and
 * floored at 1 so a test that ran under a day reads "1 day", never "0 days".
 */
export function abTestRanForDays(
  startedAt: string,
  completedAt: string,
): number {
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  return Math.max(1, Math.round(ms / (24 * 60 * 60 * 1000)));
}

/**
 * What a Winner badge says on hover. `completed_at` is a *scheduled* end, so a
 * future date means the winner is promoted and still measuring — not ended.
 * `startedAt` lets an ended test report how long it ran; omit it and the run
 * length is left off rather than guessed.
 */
export function abTestWinnerTooltip(
  completedAt: string | null,
  startedAt: string | null,
): string {
  if (completedAt == null) {
    return "Promoted - sending to all future recipients";
  }
  if (!hasTestEnded({ completedAt })) {
    return `Promoted - measuring results until ${abTestEndedOn(completedAt)}`;
  }
  const days =
    startedAt == null ? null : abTestRanForDays(startedAt, completedAt);
  const ranFor =
    days == null ? "" : ` (ran for ${days} ${days === 1 ? "day" : "days"})`;
  return `A/B test completed on ${abTestEndedOn(completedAt)}${ranFor}`;
}

/**
 * Stands in for any A/B cell with no value. Shared by every formatter and row
 * below so the results card, its details-drawer table and the arm rows can't
 * drift onto different placeholders.
 */
export const EMPTY_CELL = "--";

/** Format a 0–1 rate as a percentage: whole number at ≥10%, one decimal below. */
export function formatRate(value: number | null | undefined): string {
  if (value == null) return EMPTY_CELL;
  const pct = value * 100;
  return pct >= 10 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

const RATE_METRIC_LABELS: Record<string, string> = {
  email_opened: "Open rate",
  email_clicked: "Click rate",
  sms_clicked: "Click rate",
  push_opened: "Open rate",
};

// The two sit on one page and disagree, so say why.
const ENGAGEMENT_MISMATCH_TOOLTIP =
  "Might differ from the Engagement card which divides by messages delivered and not messages sent.";

// Only where the Engagement card divides by delivered — email and SMS. Push has
// no provider "delivered" event, so its card rate is over sent (the A/B basis
// too), and there's no mismatch to explain. See use-aggregated-message-stats.ts.
const DELIVERED_DENOMINATOR_METRICS: Record<string, true> = {
  email_opened: true,
  email_clicked: true,
  sms_clicked: true,
};

export function metricRateTooltip(
  managedModelType: string | null | undefined,
): string | undefined {
  // `in` would report true for every Object.prototype member.
  if (
    managedModelType == null ||
    !Object.hasOwn(DELIVERED_DENOMINATOR_METRICS, managedModelType)
  ) {
    return undefined;
  }
  return ENGAGEMENT_MISMATCH_TOOLTIP;
}

/**
 * Friendly rate label for a metric: managed models get a channel-agnostic name
 * ("Open rate"), custom metrics fall back to "<name> rate". Shared by the
 * results card and the details-drawer table so the two label metrics the same.
 */
export function metricRateLabel(
  managedModelType: string | null | undefined,
  name: string | null | undefined,
): string {
  const managed = managedModelType
    ? RATE_METRIC_LABELS[managedModelType]
    : undefined;
  return managed ?? (name ? `${name} rate` : "Rate");
}
